import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { api, ApiError, idempotent, newRequestId, type ShoppingCommandDto, type ShoppingCommandsResult } from "../api/client";

/**
 * Offline-Vormerkung für Einkaufsbefehle. Jede vorgemerkte Aktion behält ihre stabile Request-ID, sodass
 * Wiederholungen nach Verbindungsabbruch keine doppelte Änderung erzeugen. Konflikte (Version, Einheiten)
 * werden nicht still überschrieben, sondern zur Entscheidung angezeigt.
 */
export type PendingShopping = {
  id: string;
  listId: string;
  commands: ShoppingCommandDto[];
  createdAt: string;
  state: "pending" | "conflict";
  error?: string;
};

interface PlannerDb extends DBSchema {
  pendingShopping: { key: string; value: PendingShopping; indexes: { byCreated: string } };
  snapshots: { key: string; value: { key: string; savedAt: string; data: unknown } };
}

let dbPromise: Promise<IDBPDatabase<PlannerDb>> | null = null;

function db(): Promise<IDBPDatabase<PlannerDb>> {
  dbPromise ??= openDB<PlannerDb>("familyplanner", 1, {
    upgrade(d) {
      const s = d.createObjectStore("pendingShopping", { keyPath: "id" });
      s.createIndex("byCreated", "createdAt");
      d.createObjectStore("snapshots", { keyPath: "key" });
    },
  });
  return dbPromise;
}

const listeners = new Set<() => void>();

export function onQueueChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const notify = () => listeners.forEach((l) => l());

let lastCreated = 0;

/** Streng aufsteigender Zeitstempel: zwei Aktionen in derselben Millisekunde behalten ihre Reihenfolge. */
function nextTimestamp(): string {
  lastCreated = Math.max(Date.now(), lastCreated + 1);
  return new Date(lastCreated).toISOString();
}

export async function enqueueShopping(listId: string, commands: ShoppingCommandDto[]): Promise<PendingShopping> {
  const entry: PendingShopping = { id: newRequestId("shop"), listId, commands, createdAt: nextTimestamp(), state: "pending" };
  await (await db()).put("pendingShopping", entry);
  notify();
  return entry;
}

export async function listPending(): Promise<PendingShopping[]> {
  return (await db()).getAllFromIndex("pendingShopping", "byCreated");
}

export async function discardPending(id: string): Promise<void> {
  await (await db()).delete("pendingShopping", id);
  notify();
}

export type FlushResult = { sent: number; conflicts: number; stoppedOffline: boolean; completed: { requestId: string; result: ShoppingCommandsResult }[] };

type Sender = (entry: PendingShopping) => Promise<ShoppingCommandsResult>;

const defaultSender: Sender = (entry) =>
  api<ShoppingCommandsResult>(`/api/v1/shopping-lists/${entry.listId}/commands`, { body: { commands: entry.commands }, headers: idempotent(entry.id) });

let flushing: Promise<FlushResult> | null = null;

/** Überträgt vorgemerkte Aktionen in Reihenfolge. Läuft nie parallel zu sich selbst. */
export function flushShopping(send: Sender = defaultSender): Promise<FlushResult> {
  flushing ??= (async () => {
    const result: FlushResult = { sent: 0, conflicts: 0, stoppedOffline: false, completed: [] };
    try {
      for (const entry of await listPending()) {
        if (entry.state === "conflict") {
          result.conflicts++;
          continue;
        }
        try {
          const response = await send(entry);
          result.completed.push({ requestId: entry.id, result: response });
          await (await db()).delete("pendingShopping", entry.id);
          result.sent++;
        } catch (e) {
          if (e instanceof ApiError && (e.isOffline || e.retryable)) {
            result.stoppedOffline = true;
            break;
          }
          const message = e instanceof ApiError ? `${e.message} (${e.code})` : "Unbekannter Fehler";
          await (await db()).put("pendingShopping", { ...entry, state: "conflict", error: message });
          result.conflicts++;
        }
      }
    } finally {
      flushing = null;
      notify();
    }
    return result;
  })();
  return flushing;
}

export async function saveSnapshot(key: string, data: unknown): Promise<void> {
  await (await db()).put("snapshots", { key, savedAt: new Date().toISOString(), data });
}

export async function loadSnapshot<T>(key: string): Promise<{ savedAt: string; data: T } | undefined> {
  const s = await (await db()).get("snapshots", key);
  return s ? { savedAt: s.savedAt, data: s.data as T } : undefined;
}

/** Beim Abmelden: alle lokalen privaten Daten löschen. */
export async function clearLocalData(): Promise<void> {
  const d = await db();
  await d.clear("pendingShopping");
  await d.clear("snapshots");
  if ("caches" in globalThis) {
    for (const key of await caches.keys()) {
      await caches.delete(key);
    }
  }
  notify();
}
